import { motion } from 'framer-motion'
import { AppSettingsContext } from 'global/cnr/contexts/AppSettingsContext'
import { BaseSettingsContext } from 'global/cnr/contexts/BaseSettingsContext'
import { FrameworkContext } from 'global/cnr/contexts/FrameworkContent'
import { ParentContext } from 'global/cnr/contexts/ParentContext'
import { SettingsAreaContext } from 'global/cnr/contexts/SettingsAreaContext'
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess'
import { useBreadcrumbs } from 'global/cnr/contexts/useHelpers/useBreadcrumbs'
import { useExternal } from 'global/cnr/contexts/useHelpers/useExternal'
import { useSettings } from 'global/cnr/contexts/useHelpers/useSettings'
import { dataFix } from 'global/common/dataFix'
import UiSaveButtons from 'global/components/buttons/UiSaveButtons'
import TreeViewer from 'global/components/viewers/TreeViewer'
import { _allowUpdates_settings } from 'global/firestoreData/settings/updateSettings'
import SettingsConsole from 'global/viewSettings/components/menus/SettingsConsole'
import SettingsActionItems from 'global/viewSettings/components/SettingsActionItems'
import DataConstraintBuilder from 'global/viewSettings/create/createSubs/DataConstraintBuilder'
import { vsComponentTypes } from 'global/viewSettings/enums/vsComponentTypes'
import _ from 'lodash'
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Icon, Label } from 'semantic-ui-react'
import { squaresHandlers, squaresInitialState, squaresReducer } from '../cnr/reducers/SquaresReducer'
import SquaresForm from './SquaresForm'
import { _squareTypes, SquareSquares } from './SquareSquares'
import Wrapper, { wrapperTypes } from './Wrapper'

const itemTransition = { duration: 0.25, ease: 'easeOut' };
const _formTypes = ['themeColors']
const _showLog = false

export const SquaresContext = createContext()

export const SquaresWrapper = () => {

  // appUserAccess
  const appUserAccess = useAppUserAccess()

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { appForms_state, currentSettings_state, paps_state } = states ?? {}
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { projectModules, projectOptions, pageOptions, productionSettings } = aps_global ?? {}
  const { view, viewKey, pathViews } = paps_state ?? {}
  const { appForms } = appForms_state ?? {}

  // baseSettingsContext
  const baseSettingsContext = useContext(BaseSettingsContext)
  const { baseSettings_state } = baseSettingsContext ?? {}
  const { baseSettings_ammended } = baseSettings_state ?? {}

  // settingsAreaContext
  const settingsAreaContext = useContext(SettingsAreaContext);
  const { settingsArea_state, settingsArea_handlers } = settingsAreaContext ?? {}
  const { menuAuthGroups, isGlobal, nonLandingView, baseSettings_combined } = settingsArea_state ?? {}
  const { handleCloseGroupItem } = settingsArea_handlers ?? {}

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_handlers } = frameworkContext ?? {}

  // appSettingsContext
  const appSettingsContext = useContext(AppSettingsContext);
  const { appSettings_handlers } = appSettingsContext ?? {}

  const handleSettingsOn = () => {
    if (isGlobal) {
      framework_handlers.handleShow_frameworkLeft()
      // appSettings_handlers.handleToggle_settingsLeftOn()
    } else {
      framework_handlers.handleShow_frameworkRight()
      // appSettings_handlers.handleToggle_settingsRightOn()
    }
  }

  const _basePageView = view === 'landing' ? pathViews?.events ? 'events' : pathViews?.clients ? 'clients' : 'home' : view
  const _pageView = viewKey ? _basePageView : `${view}_list`

  const _settingsDocName = pathViews?.events || pathViews?.clients || 'home'

  // settingsMenuContext
  // const settingsMenuContext = useContext(SettingsMenuContext);
  // const { settingsMenu_fns } = settingsMenuContext ?? {}
  // const { allowProjectModule } = settingsMenu_fns ?? {}

  const settings_fns = useSettings()
  const { handleOpenSettingsItem } = useExternal({ pathViews, productionSettings })

  const baselineRef = useRef(null);

  useEffect(() => {
    if (!baselineRef.current && currentSettings_state?.gvviSettings) {
      baselineRef.current = structuredClone(
        currentSettings_state.gvviSettings
      );
    }
  }, [currentSettings_state]);

  if (!baselineRef.current) {
    baselineRef.current = _.get(currentSettings_state, 'gvviSettings', { aps_global: {}, aps_viewItems: {}, aps_views: {} });
  }

  const baseline = baselineRef.current;

  const [squares_state, dispatch] = useReducer(squaresReducer, squaresInitialState(baseline, isGlobal, _pageView, view, viewKey, menuAuthGroups, _basePageView));
  const squares_handlers = squaresHandlers(dispatch);

  const {
    allGroups,
    apsGlobal,
    apsViewItems,
    apsViews,
    currentViProps,
    dataHasChanged,
    diffCount,
    diffs,
    direction,
    showCommit,
  } = squares_state

  const _allowUpdates = diffCount && diffCount > 0

  const [updating, setUpdating] = useState()

  const currentGroup = allGroups?.[allGroups.length - 1] || null
  const isMain = _.size(allGroups) === 1

  const handleUpdate_data = async () => {
    const { dataToUpdate } = diffs
    const dtu = dataFix.cleanDocumentData(dataToUpdate)
    setUpdating(true)
    // // datasToUpdate, settingsDocName, appUser, callback
    await settings_fns.updateSettings_updatesOnly(dtu, _settingsDocName, appUserAccess)
    setUpdating()
    squares_handlers.handleClose_diffs()
  }

  const handle_formUpdate = useCallback((localData, cp, cpf, goBack) => {
    squares_handlers.handleSet_value(localData, cp, cpf)
    goBack && squares_handlers.handleGo_back()
  }, [apsViewItems, apsGlobal]);

  const handle_goNext = (item, group) => {
    const { triggerUrl } = item
    if (triggerUrl) {
      handleOpenSettingsItem({ triggerUrl })
      return;
    }
    squares_handlers.handleGo_next(item, group?.key)
  };

  const backLinks = useMemo(() => {
    if (!allGroups) return;
    const bl = _.map(
      allGroups?.slice(0, -1),
      (group, index) => (
        {
          key: group.key || 0,
          caption: group.caption || _.startCase(group?.key) || 'App Settings'
        }
      )
    );
    return bl
  }, [allGroups])

  const bcrb = useBreadcrumbs({ items: backLinks, onClick: squares_handlers.handleGo_backTo })

  const { currentData, currentPath, currentPathFull } = useMemo(() => {

    const keys = _.map(allGroups, 'key');
    const isViewItem = keys.includes('views');
    const isViewItems = keys.includes('viewItems');

    const buildPath = (skipCount) =>
      _.chain(allGroups)
        .slice(skipCount)
        .map('key')
        .filter(Boolean)
        .join('.')
        .value();

    const dataPath_f = buildPath(0);
    const dataPath_g = buildPath(2);
    const dataPath_vi = buildPath(3);
    const dataPath_v = buildPath(2);

    let _currentData;
    let _currentPath;

    if (isGlobal) {
      _currentData = isViewItem ? _.cloneDeep(apsViewItems) : _.cloneDeep(apsGlobal);
      _currentPath = isViewItem ? dataPath_vi : dataPath_g;
    } else {
      _currentData = _.cloneDeep(apsViews);
      _currentPath = isViewItems ? `${_pageView}.viewItems.${dataPath_v}` : `${_pageView}.${dataPath_v}`;
    }

    const _pathData = _.get(_currentData, _currentPath, {})

    return {
      currentData: _pathData,
      currentPath: _currentPath,
      currentPathFull: dataPath_f
    };
  }, [currentGroup, apsViews]);

  _showLog && console.log('currentData', currentData)

  const header_top = () => {
    const { caption, key } = currentGroup
    const _caption = caption || _.startCase(key)

    return <div className='swr-header'>
      {bcrb}
      <div className='swr-header-cap'><Icon color={'blue'} name='arrow left' onClick={(e) => squares_handlers.handleGo_back()}></Icon>{_caption}</div>
    </div>
  }

  const footer_main = () => {
    const btns = []
    if (showCommit) {
      btns.push({ oc: squares_handlers.handleClose_diffs, caption: 'Close', icon: 'delete', fl: true, color: 'grey' })
      btns.push({ oc: handleUpdate_data, caption: `Update (${diffCount || 0})`, icon: 'save', color: _allowUpdates_settings ? 'green' : 'teal', disabled: !_allowUpdates })
    } else {
      btns.push({ oc: squares_handlers.handleReset_value, caption: 'Reset', icon: 'ban', color: 'grey', fl: true, disabled: !_allowUpdates })
      btns.push({ oc: squares_handlers.handleShow_commit, caption: `Show Changes (${diffCount || 0})`, icon: 'arrow right', disabled: !_allowUpdates })
    }
    return <UiSaveButtons others={btns} />
  }

  const wrapper = (content, key) => <Wrapper
    header={!isMain && header_top()}
    content={motion_wrapper(content, key)}
    wrapperType={wrapperTypes.padded}
  />

  const motion_wrapper = (content, formKey) => <motion.div
    className={'mwsw'}
    key={formKey || 'key'}
    layout
    initial={{ opacity: 0, y: 0, x: getX(direction) }}
    animate={{ opacity: 1, y: 0, x: 0 }}
    exit={{ opacity: 0, y: 0, x: getX(direction) }}
    transition={itemTransition}>
    {content}
  </motion.div>

  /**
   * 
   * @param {string} squareType 
   * @param {object} currentGroup 
   * @returns a set of squares from SquareSquares
   */
  const squares_any = (squareType, currentGroup) => {
    const ss = <SquareSquares
      _pageView={_pageView}
      allGroups={allGroups}
      currentData={currentData}
      currentGroup={currentGroup}
      direction={direction}
      handle_goNext={handle_goNext}
      handleUpdate_data={handleUpdate_data}
      isGlobal={isGlobal}
      isMain={isMain}
      squareType={squareType}
    />
    return { squares: ss }
  }

  const squares_alt = (key_group, formItemType) => {
    const ss = []
    switch (key_group) {
      case 'baseSettings':
      case 'settingsActions':
        ss.push(<SettingsActionItems isGlobal={isGlobal} viType={key_group} />)
        break;
      case vsComponentTypes.globalSettings:
      case vsComponentTypes.groupSettings:
      case vsComponentTypes.pageSettings:
      case vsComponentTypes.pageItemSettings:
      case vsComponentTypes.propSettings:
      case vsComponentTypes.propSectionSettings:
      case vsComponentTypes.dataModes:
        ss.push(<SettingsConsole vsComponentType={key_group} />)
        break;
      case 'dataConstraintList':
      case 'dataMatchConstraintList':
      case 'appUserDataConstraintList':
      case vsComponentTypes.constraintBuilder:
        ss.push(<DataConstraintBuilder
          formItemType={formItemType}
          currentGroup={currentGroup}
          allGroups={allGroups}
          currentPath={currentPath}
          currentPathFull={currentPathFull}
          currentViProps={currentViProps}
          currentData={currentData}
          handle_formUpdate={handle_formUpdate}
        />)
        break;
    }
    return { squares: wrapper(ss) }
  }

  const squares_non = (key_group) => {
    const ss = <div>{'NON ' + key_group}</div>
    return { squares: wrapper(ss) }
  }


  const squares_form_childMenuItem = (currentGroup) => {
    const x = []
    const { childMenuItem } = currentGroup
    const cmi = {
      allowedItems: null,
      groupItems: null
    }
    const { allowedItems, groupItems } = _.get(baseSettings_state, `baseSettings_ammended.global.${childMenuItem}`, {});
    if (groupItems) {
      cmi.allowedItems = allowedItems
      cmi.groupItems = groupItems
    } else {
      const { allowedItems: allowedItems_pi, groupItems: groupItems_pi } = _.get(baseSettings_state, `baseSettings_ammended.pageItem.${childMenuItem}`, {});
      if (groupItems_pi) {
        cmi.allowedItems = allowedItems_pi
        cmi.groupItems = groupItems_pi
      }
    }
    const _allowedItems = cmi?.allowedItems?.[currentGroup?.key]
    const filteredGroupItems = _allowedItems ? _.pick(cmi?.groupItems, _allowedItems) : cmi?.groupItems;

    const _currentGroup = {
      groupItems: filteredGroupItems,
      key: currentGroup.key
    }

    const f = <SquaresForm
      handle_formUpdate={handle_formUpdate}
      allGroups={allGroups}
      currentData={currentData}
      currentGroup={_currentGroup}
      currentPath={currentPath}
      currentPathFull={currentPathFull}
      currentViProps={currentViProps}
    />
    x.push(f)
    return { squares: wrapper(x) }
  }

  const squares_form = (currentGroup) => {
    const x = []
    const f = <SquaresForm
      handle_formUpdate={handle_formUpdate}
      currentGroup={currentGroup}
      allGroups={allGroups}
      currentData={currentData}
      currentPath={currentPath}
      currentViProps={currentViProps}
      currentPathFull={currentPathFull}
    />
    x.push(f)
    return { squares: wrapper(x) }
  }

  const squares_form_test = (parentGroupKey) => {
    const x = []
    const y = getSettingsBaseItem(settingsArea_state, parentGroupKey)
    const cg = {
      groupItems: y,
      caption: _.startCase(parentGroupKey)
    }
    const f = <SquaresForm
      handle_formUpdate={handle_formUpdate}
      currentGroup={cg}
      allGroups={allGroups}
      currentData={currentData}
      currentPath={currentPath}
      currentViProps={currentViProps}
      currentPathFull={currentPathFull}
    />
    x.push(f)
    return { squares: wrapper(x) }
  }

  const { squares } = useMemo(() => {

    // when the currentGroup changes...
    if (currentGroup) {

      // depending on the currentGroup, return the corresponding squares!!!

      const { key: key_group, groupItems, childItems, childMenuItem, parentGroupKey, isChildItem } = currentGroup

      const isForm = _.some(groupItems, item => _.has(item, 'data')) || _.includes(_formTypes, key_group)

      if (_showLog) {
        console.group('All Groups', key_group)
        console.log('allGroups', allGroups)
        console.log('currentGroup', currentGroup)
        console.log('parentGroupKey', parentGroupKey)
        console.log('currentData', currentData)
        console.log('key_group', key_group)
        console.log('isForm', isForm)
        console.groupEnd()
      }

      let _squareType;

      if (_.size(allGroups) === 1) return squares_any(_squareTypes.main, currentGroup)
      if (childItems) return squares_any(_squareTypes.childItem, currentGroup)
      if (childMenuItem) return squares_form_childMenuItem(currentGroup)

      // if (isChildItem) {
      //   return squares_form_test(key_group)
      // }

      switch (parentGroupKey) {
        case 'views':
          return squares_any(_squareTypes.pageItem, currentGroup)
        case 'viewItems':
          return squares_any(_squareTypes.pageItems, currentGroup)
        case 'props':
          return squares_any(_squareTypes.props, currentGroup)
        case 'propSections':
          return squares_any(_squareTypes.propSection, currentGroup)
        case 'files':
          return squares_any(_squareTypes.files, currentGroup)
        case 'settingsConsole':
          return squares_alt(key_group)
        case 'uiElements':
        case 'uiSections':
          return squares_form_test(key_group)
        // return squares_any(_squareTypes.files, currentGroup) 
        default:
        // nothing - keep going
      }

      switch (key_group) {
        case 'views':
          return squares_any(_squareTypes.pages_global, currentGroup)
        case 'viewItems':
          return squares_any(_squareTypes.pages_page, currentGroup)
        case 'props':
          if (currentGroup.isAlt) {
            return squares_any(_squareTypes.pageSections, currentGroup)
          } else {
            return squares_any(_squareTypes.pageProps, currentGroup)
          }
        case 'propSections':
          return squares_any(_squareTypes.pageSections, currentGroup)
        // case 'propSections':
        case 'baseSettings':
        case 'settingsActions':
          return squares_alt(key_group)

        case 'constraintBuilder':
        case 'dataConstraintList':
        case 'dataMatchConstraintList':
        case 'appUserDataConstraintList':
        case 'dataConstraintList':
          return squares_alt(key_group, currentGroup?.formItemType)
        default:
          if (isForm) {
            return squares_form(currentGroup)
          } else if (!_.isEmpty(groupItems)) {
            // need to filter some things
            switch (key_group) {
              case 'pageOptions':
                currentGroup.groupItems = getFiltered_pageOptions(currentGroup, pageOptions)
              default:
              // nothing
            }
            return squares_any(_squareTypes.grouped, currentGroup)
            // return  squares_grouped(currentGroup)
          } else {
            return squares_non(key_group)
          }
      }
    }

    return {}

  }, [currentGroup])

  const wrapper_commit = () => <Wrapper
    header={!isMain && header_top()}
    content={diffs?.data ? <TreeViewer data={diffs?.data} scNames={true} /> : <div>No Changes</div>}
    updating={updating}
    wrapperType={wrapperTypes.padded}
  />

  const header_content = () => <div style={{ display: 'flex', flexDirection: 'row', rowGap: '2em', justifyContent: 'space-between', alignItems: 'center' }}>
    <div style={{ display: 'flex', flexDirection: 'row', columnGap: '1em', justifyContent: 'space-between' }}>
      <div><Icon name='arrow left' onClick={(e) => handleSettingsOn()} /></div>
      <div>{isGlobal ? 'App Settings' : `Page Settings - ${_.startCase(nonLandingView)}`}</div>
    </div>
    <div><Label style={{ cursor: 'pointer' }} onClick={(e) => squares_handlers.handleShow_commit()} ><Icon name='save' color={'blue'} disabled={!_allowUpdates} />{`Update (${diffCount || 0})`}</Label></div>
  </div>

  const squares_content = () => <SquaresContext.Provider value={{ squares_state, squares_handlers }}>
    {squares}
  </SquaresContext.Provider>

  const wrapper_content = () => <Wrapper
    header={header_content()}
    content={showCommit ? wrapper_commit() : squares_content()}
    footer={footer_main()}
    updating={updating}
    wrapperType={wrapperTypes.padded}
  ></Wrapper>

  return wrapper_content()

}

const getX = (direction) => {
  switch (direction) {
    case 'left':
      return -20; // enters from left
    case 'right':
      return 20;  // enters from right
    default:
      return 0;
  }
};


const getFiltered_pageOptions = (currentGroup, pageOptions) => {
  const { groupItems } = currentGroup
  const filtered = _.pickBy(groupItems, (item, key) => {
    const _key = 'allow' + _.upperFirst(key)
    let allow = pageOptions?.[_key]
    if (allow) {
      return item
    }
  });
  return filtered

}

const getSettingsBaseItem = (settingsArea_state, key, si) => {
  const { baseSettings_combined = {} } = settingsArea_state ?? {}
  const { pageItem, global, prop, propSection } = baseSettings_combined

  const sub = si || 'groupItems'

  const sources = [global, pageItem, prop, propSection]

  for (const source of sources) {
    if (source?.[key]?.[sub]) {
      return _.cloneDeep(source[key][sub])
    }
  }
}
